import { PocWorkflowOverlay } from 'commons/components/components-wrapper';
import { owcTheme } from 'core/theme/theme.config';
import styled from 'styled-components';

export const WorkflowStyledComponent = styled(PocWorkflowOverlay)`
    /* OWC-OVERRIDE: v6's header inset. owc-workflow-overlay exposes no header-padding prop in
       v7.6.0 and its header wrapper is not slottable. */
    .owc-workflow-overlay__backdrop-element__overlay-element__header-wrapper.owc-component-header {
        padding-left: 0.25rem;
    }

    /* OWC-OVERRIDE: v7 adds a padding band on the content wrapper; restore v6's edge-to-edge
       content. !important beats owc's scoped rule, and there is no content-padding prop in
       v7.6.0. Revisit on the next @one upgrade. */
    .owc-workflow-overlay__backdrop-element__overlay-element__content-wrapper {
        padding: 0 !important;
    }

    /* OWC-OVERRIDE: the border itself comes from the native separator prop, but its colour is
       fixed by owc; recolour it to v6's divider token. No separator-colour prop in v7.6.0. */
    owc-component-footer[slot='footer'].owc-component-footer--separator {
        border-top-color: ${owcTheme['one-color-gray-100']};
    }

    /* OWC-OVERRIDE: show/hide the overlay header per consumer. owc-workflow-overlay always
       renders its header wrapper in v7.6.0 and offers no headerless variant. */
    &.no-header .owc-workflow-overlay__backdrop-element__overlay-element__header-wrapper.owc-component-header {
        display: none;
    }

    &.show-header .owc-workflow-overlay__backdrop-element__overlay-element__header-wrapper.owc-component-header {
        display: flex;
    }

    .settings {
        align-items: center;
        height: 100% !important;
        background-color: var(--one-color-gray-50) !important;

        > div {
            display: flex;
            flex-direction: column;
            width: 75%;
            max-width: 788px;
        }
    }

    .side-menu {
        display: flex;
        flex-direction: column;
        width: 320px;
        min-width: 320px;
        height: 100%;
        border-right: 1px solid #c2bab5;

        .side-menu-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: 52px;
            padding: 4px 8px;
            border-bottom: 1px solid #c2bab5;

            &.settings-menu {
                justify-content: start;
            }
        }

        .side-menu-content {
            height: 100%;
            overflow: auto;

            > div {
                display: flex;
                align-items: center;
            }
        }

        .side-menu-footer {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 52px;

            #ot-sdk-btn.ot-sdk-show-settings {
                color: var(--one-color-interaction-default-brand-base);
                border: unset;
            }

            #ot-sdk-btn.ot-sdk-show-settings:hover,
            #ot-sdk-btn.optanon-show-settings:hover {
                color: var(--one-color-interaction-hover-brand-high);
                background-color: unset;
            }
        }
    }

    owc-card {
        overflow: clip;
    }

    div[slot='content'] {
        display: flex;
        flex-direction: row;
        width: 100%;
        height: 100%;
        padding: 0;
        overflow: hidden;

        &.loading {
            align-items: center;
            justify-content: center;
        }

        .workflow-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            overflow-y: auto;
        }

        .workflow-content-with-menu {
            display: flex;
            flex-direction: row;
            width: 100%;
            overflow: hidden;

            .workflow-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;
                max-width: 750px;
                height: 100%;
                overflow: auto;

                > div {
                    display: flex;
                    flex-direction: column;
                    width: 75%;
                    max-width: 750px;
                }
            }
        }
    }

    div[slot='content'].no-header {
        padding: 0;
    }

    owc-component-header[slot='header'] {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        border-bottom: solid 1px ${owcTheme['one-color-gray-100']};

        /* OWC-OVERRIDE: v7 renders the title container as display:block — restore the v6 row
           layout. owc-component-header exposes no title-layout prop in v7.6.0. */
        .owc-component-header__prefix-container__title {
            display: flex;
            flex-direction: row;
            gap: 10px;
            align-items: center;
        }

        /* Hide the stray focus ring v7 leaves on the auto-focused header buttons; :focus scopes
           this to mouse/programmatic focus so the keyboard :focus-visible ring is never removed. */
        owc-icon-button:focus:not(:focus-visible) {
            outline: none !important;
        }
    }

    div[slot='footer'] {
        display: flex;
        flex-direction: row;
        align-items: center;

        /* v7 dropped the footer's right-alignment and padding that owc provided by default; restore v6. */
        justify-content: flex-end;
        padding: 16px;
        border-top: solid 1px ${owcTheme['one-color-gray-100']};

        > owc-button:not(:last-child) {
            margin-right: 40px;
        }
    }
`;

export const WorkflowOverlaySectionLayout = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 40px;

    > div:first-child {
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 750px;
        margin-bottom: 24px;

        > owc-typography:last-child {
            color: var(--one-color-gray-400);
        }
    }
`;

export const WorkflowOverlaySectionTitleLayout = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;

    owc-typography {
        color: #706b69;
    }
`;

export const WorkflowOverlaySectionDescriptionLayout = styled.div`
    owc-typography {
        color: #706b69;
    }

    padding-bottom: 8px;
`;

export const WorkflowOverlaySectionChildrenLayout = styled.div``;

export const FullWidthContentWrapper = styled.div`
    width: 100% !important;
    max-width: none !important;
    height: 100%;
`;

export const WorkflowOverlayWithStepperLayout = styled(WorkflowStyledComponent)`
    owc-stepper {
        /* v7 sets overflow: auto on the stepper; because CSS forces a non-visible sibling axis,
           overflow-x: visible alone computes back to auto → a stray vertical scrollbar. Set both
           axes visible to remove the scroll and let the stepper render edge-to-edge. */
        overflow: visible !important;

        /* OWC-OVERRIDE: steps aren't clickable in a linear stepper; suppress owc's blue hover
           fill on the label. owc-step has no non-interactive/disabled-hover prop in v7.6.0. */
        owc-step:hover .owc-step__slot-wrapper,
        owc-step .owc-step__slot-wrapper:hover {
            background-color: transparent !important;
        }
    }

    div[slot='content'] {
        flex-direction: column;
        padding: 24px;

        > div {
            > div {
                padding: 0 69px 0 214px;
            }
        }

        owc-table {
            > owc-card {
                owc-table-body {
                    height: 60vh;
                    overflow: auto;
                }

                owc-component-footer[slot='footer'] {
                    padding: 0.5rem 1rem;
                }

                @media (height <= 1024px) {
                    owc-table-body {
                        height: 45vh;
                    }
                }

                @media (height <= 800px) {
                    owc-table-body {
                        height: 30vh;
                    }
                }
            }
        }
    }
`;
